import {
  Action,
  BoundActions,
  createHook,
  createStore,
} from "react-sweet-state";

import { authenticatedGetFetch } from "utility/authenticated_fetch";
import { GenericObject } from "utility/misc";
import { promiser } from "utility/promiser";
import { trying } from "utility/trying";

// -----------------------------------------------------------------------------

export enum BillingLoadState {
  Unknown,
  Error,
  Loading,
  Success,
}

// -----------------------------------------------------------------------------

const initialState: BillingStoreState = Object.freeze({
  state: BillingLoadState.Unknown,
});

// -----------------------------------------------------------------------------

const BillingStore = createStore({
  initialState,
  actions: {
    load,
  },
});

export const useBillingStore = createHook(BillingStore);

// -----------------------------------------------------------------------------

function load(): Action<BillingStoreState> {
  return async ({ setState }) =>
    await promiser(
      "BillingStore.load",
      async () =>
        await trying(
          async () => {
            setState({ state: BillingLoadState.Loading });
            const responsePayload = await authenticatedGetFetch(
              "api/billing/info",
              {
                csrf: true,
              }
            );
            setState({
              state: BillingLoadState.Success,
              ...responsePayload,
              error: undefined,
            });

            return responsePayload;
          },
          `BillingStore:load failed`,
          (error: Error) =>
            setState({
              state: BillingLoadState.Error,
              subscription: undefined,
              invoices: undefined,
              error: error?.message ?? undefined,
            })
        )
    );
}

// -----------------------------------------------------------------------------

export interface BillingStoreState {
  state: BillingLoadState;
  error?: string;
  subscription?: Subscription;
  invoices?: Invoice[];
}

export type BillingStoreActions = BoundActions<
  BillingStoreState,
  typeof BillingStore.actions
>;

interface Subscription extends GenericObject {
  label?: string;
  is_trial?: boolean;
  days_left?: number;
  expires?: number;
}

interface Invoice extends GenericObject {}
